import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getActiveJobs } from "../api";
import { cleanApiValues } from "../utils/cleanApiValues";

// One shared empty list: a fresh `[]` on every render while loading would change the
// `jobs` identity each time and re-run every memo/effect that depends on it.
const EMPTY_JOBS = [];

const STALE_TIME = 5 * 60 * 1000;

// Map backend job data to frontend format
const mapJobToFrontend = (rawJob, idx) => {
  // "None"/"null" strings from the API are missing values, not text to show
  const job = cleanApiValues(rawJob);
  let salaryValue = '0';
  let salaryDisplay = '';

  if (job.minSalary || job.maxSalary) {
    const minSal = job.minSalary ? Number(job.minSalary) : 0;
    const maxSal = job.maxSalary ? Number(job.maxSalary) : 0;

    if (minSal && maxSal && minSal !== maxSal) {
      salaryValue = String(maxSal);
      salaryDisplay = `${minSal}-${maxSal}`;
    } else if (maxSal) {
      salaryValue = String(maxSal);
    } else if (minSal) {
      salaryValue = String(minSal);
    }
  } else if (job.salary) {
    salaryValue = String(job.salary).replace(/[^\d]/g, '') || '0';
  }

  return {
    id: job.id || job.assessmentId || `job-${idx}`,
    assessmentId: job.assessmentId || job.id,
    companyId: job.companyId,
    name: job.name || job.job || 'Untitled Position',
    companyName: job.companyName || 'Company',
    // No Diil-mark fallback here — a company without a photo is meant to show the
    // plain company icon everywhere it's rendered (see components/CompanyPhoto.jsx),
    // not a placeholder image that reads as the ad belonging to Diil itself.
    logo: job.companyPhoto || null,
    createdDate: job.createdDate || new Date().toISOString(),
    postedDate: job.postedDate || null,
    displayDate: job.postedDate || job.createdDate || new Date().toISOString(),
    location: job.branchAddress || job.department || 'Mongolia',
    branchAddress: job.branchAddress || null,
    departmentName: job.departmentName || job.department || null,
    coordinates:
      job.coordinates ||
      // The job's own branch, joined in by the list endpoint
      job.branchCoordinates ||
      (job.latitude != null && job.longitude != null ? `${job.latitude},${job.longitude}` : null) ||
      (job.lat != null && (job.lng != null || job.lon != null) ? `${job.lat},${job.lng ?? job.lon}` : null),
    salary: salaryValue,
    salaryDisplay: salaryDisplay,
    minSalary: job.minSalary ? Number(job.minSalary) : null,
    maxSalary: job.maxSalary ? Number(job.maxSalary) : null,
    salaryText: job.salaryText || job.salary || null,
    isLiked: false,
    description: job.description || '',
    introductionText: job.introductionText || '',
    requirementText: job.requirementText || '',
    employmentType: job.employmentType || null,
    scheduleType: job.scheduleType || null,
    workingDays: job.workingDays || null,
    hoursPerWeek: job.hoursPerWeek || null,
    scheduleDescription: job.scheduleDescription || null,
    workScheduleId: job.workScheduleId || null,
    salaryType: job.salaryType || null,
    partTimeStartDate: job.partTimeStartDate || null,
    partTimeEndDate: job.partTimeEndDate || null
  };
};

// Every input that changes which jobs come back. The query key and the request are both
// built from this, so a filter can never reach the backend without also being part of the
// cache key (or the other way round) — which is how workingDays once went missing.
const toRequestParams = (page, pageSize, filters) => ({
  page: page - 1,
  size: pageSize,
  categoryIds: filters.categoryIds,
  sortBySalary: filters.sortBySalary,
  sortOrder: filters.sortOrder,
  industryIds: filters.industryIds,
  minSalary: filters.minSalary,
  maxSalary: filters.maxSalary,
  jobLevelIds: filters.jobLevelIds,
  searchQuery: filters.searchQuery,
  country: filters.country,
  employmentType: filters.employmentType,
  workingDays: filters.workingDays,
  companyIds: filters.companyIds,
});

const jobsQueryKey = (page, pageSize, filters) => ['jobs', toRequestParams(page, pageSize, filters)];

// Fetch one page. Throws on failure so React Query reports an error and retries,
// instead of caching an empty page that reads as "no jobs".
const fetchJobsPage = async (page, pageSize, filters) => {
  const response = await getActiveJobs(toRequestParams(page, pageSize, filters));
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch jobs');
  }

  // Handle both paginated response (Page<T>) and array response
  const jobsData = response.data.content || response.data;
  const jobs = (Array.isArray(jobsData) ? jobsData : []).map(mapJobToFrontend);

  const totalElements = response.data.totalElements ?? jobs.length;
  const totalPages = response.data.totalPages || Math.ceil(totalElements / pageSize) || 1;
  const isLast = response.data.last !== undefined ? response.data.last : (page * pageSize >= totalElements);

  return {
    jobs,
    totalPages,
    totalElements,
    hasMore: !isLast && jobs.length === pageSize,
    currentPage: page
  };
};

/**
 * Custom hook for fetching jobs with React Query
 * Provides automatic caching, background refetching, pagination and filtering
 * @param {number} page - Current page number (1-indexed)
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter options
 * @param {number[]} filters.categoryIds - Category IDs to filter by (1=Waiter, 2=Cashier, 3=Cook, 5=Store)
 * @param {boolean} filters.sortBySalary - Sort by salary (highest first)
 * @param {string} filters.sortOrder - "asc" for oldest first; newest first otherwise
 * @param {number[]} filters.industryIds - Industry IDs to filter by (parent industry)
 * @param {number} filters.minSalary - Minimum salary filter
 * @param {number} filters.maxSalary - Maximum salary filter
 * @param {number[]} filters.jobLevelIds - Job level IDs to filter by (experience level)
 * @param {string} filters.searchQuery - Text search query
 * @param {string} filters.country - Company country code filter (e.g., "UZ" for Uzbekistan)
 * @param {string} filters.employmentType - Employment type (e.g., "PART_TIME")
 * @param {string[]} filters.workingDays - Working days (e.g., ["MON", "WED"])
 * @param {number[]} filters.companyIds - Company IDs to filter by
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Skip the request entirely while false. Lets a
 *   caller declare a chain of fallback queries up front (hooks cannot be conditional) and
 *   only pay for the rungs it actually reaches.
 */
export function useJobs(page = 1, pageSize = 100, filters = {}, options = {}) {
  const queryClient = useQueryClient();
  const enabled = options.enabled !== false;

  const query = useQuery({
    enabled,
    queryKey: jobsQueryKey(page, pageSize, filters),
    queryFn: () => fetchJobsPage(page, pageSize, filters),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    staleTime: STALE_TIME, // Consider data fresh for 5 minutes
    retry: 1,
  });

  // Prefetch next page for smoother pagination
  const prefetchNextPage = () => {
    if (query.data?.hasMore) {
      queryClient.prefetchQuery({
        queryKey: jobsQueryKey(page + 1, pageSize, filters),
        queryFn: () => fetchJobsPage(page + 1, pageSize, filters),
        staleTime: STALE_TIME,
      });
    }
  };

  return {
    jobs: query.data?.jobs || EMPTY_JOBS,
    totalPages: query.data?.totalPages || 1,
    totalElements: query.data?.totalElements || 0,
    hasMore: query.data?.hasMore || false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    // True while the previous filter/page's result stands in for the one being fetched
    isPlaceholderData: query.isPlaceholderData,
    // Distinguishes "no results" from "not asked yet" — an empty `jobs` array reads the
    // same either way, and a caller chaining fallback queries has to tell them apart.
    isFetched: query.isFetched,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    prefetchNextPage,
  };
}

/**
 * Hook for infinite scroll pagination
 * Accumulates jobs from multiple pages
 */
export function useInfiniteJobs(pageSize = 100) {
  const queryClient = useQueryClient();

  // Use a simple query that we'll manually update for infinite scroll
  const query = useQuery({
    queryKey: ['infiniteJobs'],
    queryFn: async () => {
      try {
        const response = await getActiveJobs({ page: 0, size: pageSize });

        if (response.success && response.data) {
          const jobsData = response.data.content || response.data;
          const mappedJobs = (Array.isArray(jobsData) ? jobsData : []).map(mapJobToFrontend);

          const totalElements = response.data.totalElements || mappedJobs.length;
          const totalPages = response.data.totalPages || Math.ceil(totalElements / pageSize) || 1;

          return {
            jobs: mappedJobs,
            totalPages: totalPages,
            totalElements: totalElements,
            hasMore: response.data.last !== undefined ? !response.data.last : mappedJobs.length === pageSize,
            currentPage: 1
          };
        }

        return {
          jobs: [],
          totalPages: 1,
          totalElements: 0,
          hasMore: false,
          currentPage: 1
        };
      } catch (error) {
        console.error('Error fetching infinite jobs:', error);
        return {
          jobs: [],
          totalPages: 1,
          totalElements: 0,
          hasMore: false,
          currentPage: 1
        };
      }
    },
    staleTime: STALE_TIME,
  });

  // Load more function for infinite scroll
  const loadMore = async () => {
    if (!query.data?.hasMore || query.isFetching) return;

    const nextPage = query.data.currentPage + 1;
    const response = await getActiveJobs({ page: nextPage - 1, size: pageSize });

    if (response.success && response.data) {
      const jobsData = response.data.content || response.data;
      const mappedJobs = (Array.isArray(jobsData) ? jobsData : []).map(mapJobToFrontend);

      // Update cache with accumulated jobs
      queryClient.setQueryData(['infiniteJobs'], (old) => ({
        jobs: [...(old?.jobs || []), ...mappedJobs],
        totalPages: response.data.totalPages || 1,
        totalElements: response.data.totalElements || 0,
        hasMore: !response.data.last,
        currentPage: nextPage
      }));
    }
  };

  return {
    jobs: query.data?.jobs || EMPTY_JOBS,
    totalPages: query.data?.totalPages || 1,
    totalElements: query.data?.totalElements || 0,
    hasMore: query.data?.hasMore || false,
    currentPage: query.data?.currentPage || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    loadMore,
  };
}

export default useJobs;
